"use client";

import { Button, Dialog, Field, TextArea } from "@hisani/ui";
import { useTranslations } from "next-intl";
import React, { type FormEvent, useId, useState } from "react";
import { STOPWATCH_COMMENT_MAX_LENGTH } from "./stopwatch-storage";

export function StopwatchCommentDialog({
  title,
  description,
  initialComment,
  submitLabel,
  submitting,
  error,
  onClose,
  onSubmit,
}: {
  readonly title: string;
  readonly description: string;
  readonly initialComment: string | null;
  readonly submitLabel: string;
  readonly submitting: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (comment: string | null) => void;
}) {
  const t = useTranslations("Stopwatch");
  const formId = useId();
  const [comment, setComment] = useState(initialComment ?? "");

  const trimmedComment = comment.trim();

  return (
    <Dialog
      open
      title={title}
      description={description}
      closeLabel={t("closeDialog")}
      closeDisabled={submitting}
      onClose={onClose}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="accent"
            loading={submitting}
          >
            {submitLabel}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          onSubmit(trimmedComment.length === 0 ? null : trimmedComment);
        }}
      >
        <Field
          label={t("commentFieldLabel")}
          labelSuffix={t("optional")}
          hint={t("commentFieldHint", {
            remaining: STOPWATCH_COMMENT_MAX_LENGTH - comment.length,
          })}
        >
          {(field) => (
            <TextArea
              {...field}
              data-dialog-autofocus
              rows={4}
              maxLength={STOPWATCH_COMMENT_MAX_LENGTH}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
            />
          )}
        </Field>
        {error === null ? null : (
          <p className="mt-3 text-sm font-semibold text-danger" role="alert">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
